import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import '../../assets/scss/bandeja-mensajes/ClientesPanel.scss';
import { Badge } from 'react-bootstrap';
import axios from 'axios';

// 🚀 FUNCIÓN OPTIMIZADA PARA FORMATEAR FECHA
const formatLastMessageTime = (timestamp) => {
  if (!timestamp) return '';
  
  try {
    const date = new Date(timestamp);
    if (isNaN(date.getTime())) return '';
    
    const now = new Date();
    const diff = now.getTime() - date.getTime();
    const hours = diff / (1000 * 60 * 60);
    const days = diff / (1000 * 60 * 60 * 24);
    
    if (hours < 1) {
      const minutes = Math.floor(diff / (1000 * 60));
      return minutes <= 1 ? 'Ahora' : `${minutes}m`;
    } else if (hours < 24) {
      return `${Math.floor(hours)}h`;
    } else if (days < 7) {
      return `${Math.floor(days)}d`;
    } else {
      return date.toLocaleDateString('es-PE', { day: '2-digit', month: '2-digit' });
    }
  } catch (error) {
    console.warn('Error formateando fecha:', error);
    return '';
  }
};

// 🚀 FUNCIÓN OPTIMIZADA PARA ORDENAMIENTO
const sortNumeros = (numeros) => {
  return [...numeros].sort((a, b) => {
    // Priorizar mensajes no leídos
    if (a.nuevos_mensajes > 0 && b.nuevos_mensajes === 0) return -1;
    if (b.nuevos_mensajes > 0 && a.nuevos_mensajes === 0) return 1;
    
    // Luego por fecha más reciente
    const dateA = new Date(a.ultimo_mensaje_timestamp);
    const dateB = new Date(b.ultimo_mensaje_timestamp);
    
    if (isNaN(dateA.getTime()) && isNaN(dateB.getTime())) return 0;
    if (isNaN(dateA.getTime())) return 1;
    if (isNaN(dateB.getTime())) return -1;
    
    return dateB.getTime() - dateA.getTime();
  });
};

// 🚀 FUNCIÓN PARA MARCAR MENSAJES COMO LEÍDOS
const markMessagesAsRead = async (phoneNumber) => {
  if (!phoneNumber) {
    console.warn(`⚠️ [ClientesPanel] Parámetros inválidos: phoneNumber=${phoneNumber}`);
    return false;
  }
  
  try {
    console.log(`📖 [ClientesPanel] Marcando mensajes como leídos para teléfono ${phoneNumber}`);
    
    const response = await axios.post(`/conversations/markAsRead/${phoneNumber}`, {}, {
          timeout: 3000,
          headers: { 'Content-Type': 'application/json' }
        });
    
    console.log(`📝 [ClientesPanel] Respuesta del servidor:`, response.data);
    
    if (response.data?.success) {
      const affectedRows = response.data.affectedRows || 0;
      console.log(`✅ [ClientesPanel] ${affectedRows} mensajes marcados como leídos exitosamente`);
      
      if (affectedRows > 0) {
        console.log(`🎯 [ClientesPanel] Se actualizaron ${affectedRows} mensajes en la base de datos`);
        return true;
      } else {
        console.log(`ℹ️ [ClientesPanel] No había mensajes no leídos para marcar`);
        return true; // No hay error, simplemente no había mensajes que marcar
      }
    } else {
      console.error(`❌ [ClientesPanel] Respuesta del servidor indica fallo:`, response.data);
      return false;
    }
  } catch (error) {
    console.error(`❌ [ClientesPanel] Error al marcar mensajes como leídos:`, {
      error: error.message,
      status: error.response?.status,
      data: error.response?.data,
      url: `/conversations/markAsRead/${phoneNumber}`
    });
    return false;
  }
};

const ClientesPanel = ({ numeros = [], selectedPhone, onSelectPhone, onMessagesRead }) => {
  const [sortedNumeros, setSortedNumeros] = useState([]);
  const [isSelecting, setIsSelecting] = useState(false);
  
  // 🚀 REFS PARA OPTIMIZACIÓN
  const isSelectingRef = useRef(false);
  const listRef = useRef(null);



  // 🚀 MEMOIZACIÓN DEL SORTING
  const memoizedSortedNumeros = useMemo(() => {
    console.log("📊 [ClientesPanel] Procesando números:", numeros.length);
    
    if (!numeros || numeros.length === 0) return [];
    
    // Log de debugging mejorado
    numeros.forEach(item => {
      if (item.nuevos_mensajes > 0) {
        console.log(`🔴 Cliente ${item.numero_telefono}: ${item.nuevos_mensajes} mensajes no leídos`);
      }
    });

    const sorted = sortNumeros(numeros);
    console.log(`✅ [ClientesPanel] ${sorted.length} clientes ordenados`);
    
    return sorted;
  }, [numeros]);

  // 🚀 ACTUALIZAR ESTADO SOLO CUANDO CAMBIE EL RESULTADO MEMOIZADO
  useEffect(() => {
    setSortedNumeros(memoizedSortedNumeros);
  }, [memoizedSortedNumeros]);

  // 🚀 FUNCIÓN OPTIMIZADA PARA SELECCIÓN
  const handleSelectPhone = useCallback(async (numeroTelefono) => {
    // Prevenir clicks múltiples
    if (isSelectingRef.current) {
      console.log('🚫 [ClientesPanel] Click múltiple detectado, ignorando');
      return;
    }

    // No cambiar si ya está seleccionado
    if (selectedPhone === numeroTelefono) {
      console.log('🚫 [ClientesPanel] Número ya seleccionado, ignorando click');
      return;
    }

    console.log(`📞 [ClientesPanel] Seleccionando cliente: ${numeroTelefono}`);
    
    isSelectingRef.current = true;
    setIsSelecting(true);

    try {
      // Llamar a la función de selección del padre
      onSelectPhone(numeroTelefono);
      
      // 🚀 MARCAR MENSAJES COMO LEÍDOS INMEDIATAMENTE AL SELECCIONAR
      if (numeroTelefono) {
        
        // **NOTIFICAR INMEDIATAMENTE PARA ACTUALIZACIÓN OPTIMISTA**
        if (onMessagesRead) {
         
          onMessagesRead(numeroTelefono);
        }
        
        // **EJECUTAR LLAMADA AL BACKEND**
        const marked = await markMessagesAsRead(numeroTelefono);
        
        if (!marked) {
          console.warn(`⚠️ [ClientesPanel] Fallo al marcar mensajes como leídos, pero UI ya se actualizó optimistamente`);
          // La actualización optimista del UI ya se ejecutó, el polling de 30s eventualmente corregirá inconsistencias
        }
      }
    } catch (error) {
      console.error(`❌ [ClientesPanel] Error al seleccionar cliente:`, error);
    } finally {
      // Permitir nuevos clicks después de un delay
      setTimeout(() => {
        isSelectingRef.current = false;
        setIsSelecting(false);
      }, 300); // Reducido para mayor responsividad
    }
  }, [selectedPhone, onSelectPhone, onMessagesRead]);

  // 🚀 RENDERIZADO OPTIMIZADO DE ELEMENTOS - DISEÑO LIMPIO SIN PUNTO VERDE
  const renderClienteItem = useCallback((item) => {
    const isSelected = selectedPhone === item.numero_telefono;
    const hasUnreadMessages = item.nuevos_mensajes > 0;
    const lastMessageTime = formatLastMessageTime(item.ultimo_mensaje_timestamp);

    return (
      <li
        key={item.numero_telefono}
        className={`cliente-item ${isSelected ? 'activo' : ''} ${hasUnreadMessages ? 'has-unread' : ''}`}
        onClick={() => handleSelectPhone(item.numero_telefono)}
        style={{ 
          cursor: isSelecting ? 'wait' : 'pointer',
          opacity: isSelecting && !isSelected ? 0.7 : 1
        }}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            handleSelectPhone(item.numero_telefono);
          }
        }}
      >
        <div className="cliente-item-content">
          {/* Avatar del cliente */}
          <div className="cliente-avatar">
            <div className={`avatar-circle ${hasUnreadMessages ? 'has-unread' : ''}`}>
              <i className="bi bi-person-fill"></i>
            </div>
          </div>

          {/* Información del cliente */}
          <div className="cliente-info">
            <div className="cliente-header">
              <div className="cliente-numero">
                {item.nombre || item.numero_telefono}
              </div>
              {lastMessageTime && (
                <div className="cliente-time">
                  {lastMessageTime}
                </div>
              )}
            </div>

            <div className="cliente-mensajes-info">
              <span className="mensaje-count">
                {item.cantidad_mensajes} mensaje{item.cantidad_mensajes !== 1 ? 's' : ''}
              </span>
              
              {/* Solo mostrar badge de mensajes no leídos, sin punto verde */}
              {hasUnreadMessages && (
                <Badge pill bg="danger" className="nuevo-mensaje-badge">
                  {item.nuevos_mensajes}
                </Badge>
              )}
            </div>
          </div>
        </div>
      </li>
    );
  }, [selectedPhone, isSelecting, handleSelectPhone]);

  const displayNumeros = sortedNumeros;
  const totalUnreadMessages = useMemo(() => 
    displayNumeros.reduce((total, item) => total + (item.nuevos_mensajes || 0), 0),
    [displayNumeros]
  );

  return (
    <div className="clientes-panel-wrapper">
      <div className="clientes-panel-header">
        <h6 className="clientes-panel-title">
          CONVERSACIONES
          {displayNumeros.length > 0 && (
            <span className="clientes-count">({displayNumeros.length})</span>
          )}
        </h6>
        {totalUnreadMessages > 0 && (
          <div className="total-unread-badge">
           
          </div>
        )}
      </div>

      {displayNumeros.length === 0 ? (
        <div className="clientes-panel-empty-state">
          <div className="empty-content">
            <div className="empty-icon">
              <i className="bi bi-chat-left-text-fill"></i>
            </div>
            <p>No hay conversaciones</p>
            <small>Las conversaciones aparecerán aquí</small>
          </div>
        </div>
      ) : (
        <div className="clientes-list-scroll-container" ref={listRef}>
          <ul className="clientes-list" role="list">
            {displayNumeros.map(renderClienteItem)}
          </ul>
        </div>
      )}

      {/* Estilos optimizados - SIN PUNTO VERDE */}
      <style jsx>{`
        .clientes-panel-wrapper {
          height: 100%;
          display: flex;
          flex-direction: column;
          background: #fff;
          border-radius: 8px;
          overflow: hidden;
          border: 1px solid #e9ecef;
        }

        .clientes-panel-header {
          padding: 1rem;
          background: #f8f9fa;
          border-bottom: 1px solid #e9ecef;
          display: flex;
          justify-content: space-between;
          align-items: center;
          flex-shrink: 0;
        }

        .clientes-panel-title {
          margin: 0;
          font-size: 0.875rem;
          font-weight: 600;
          color: #495057;
          display: flex;
          align-items: center;
          gap: 0.5rem;
        }

        .clientes-count {
          color: #6c757d;
          font-weight: 400;
        }

        .total-unread-badge {
          display: flex;
          align-items: center;
        }

        .clientes-panel-empty-state {
          flex: 1;
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 2rem 1rem;
        }

        .empty-content {
          text-align: center;
          color: #6c757d;
        }

        .empty-icon {
          font-size: 3rem;
          margin-bottom: 1rem;
          opacity: 0.5;
        }

        .empty-content p {
          margin-bottom: 0.5rem;
          font-weight: 500;
        }

        .empty-content small {
          font-size: 0.75rem;
        }

        .clientes-list-scroll-container {
          flex: 1;
          overflow-y: auto;
          scrollbar-width: thin;
          scrollbar-color: #cbd5e0 transparent;
        }

        .clientes-list-scroll-container::-webkit-scrollbar {
          width: 6px;
        }

        .clientes-list-scroll-container::-webkit-scrollbar-track {
          background: transparent;
        }

        .clientes-list-scroll-container::-webkit-scrollbar-thumb {
          background-color: #cbd5e0;
          border-radius: 3px;
        }

        .clientes-list-scroll-container::-webkit-scrollbar-thumb:hover {
          background-color: #a0aec0;
        }

        .clientes-list {
          list-style: none;
          padding: 0;
          margin: 0;
        }

        .cliente-item {
          border-bottom: 1px solid #f1f3f4;
          transition: all 0.2s ease;
          position: relative;
        }

        .cliente-item:hover:not(.activo) {
          background-color: #f8f9fa;
        }

        .cliente-item.activo {
          background-color: #e3f2fd;
          border-right: 3px solid #2196f3;
        }

        .cliente-item.has-unread {
          background-color: #fffbf0;
          border-left: 3px solid #ff9800;
        }

        .cliente-item.has-unread.activo {
          background-color: #e8f5e8;
          border-right-color: #4caf50;
          border-left-color: #4caf50;
        }

        .cliente-item-content {
          display: flex;
          align-items: center;
          padding: 0.75rem 1rem;
          gap: 0.75rem;
        }

        .cliente-avatar {
          position: relative;
          flex-shrink: 0;
        }

        .avatar-circle {
          width: 40px;
          height: 40px;
          border-radius: 50%;
          background: #e9ecef;
          display: flex;
          align-items: center;
          justify-content: center;
          color: #6c757d;
          font-size: 1.25rem;
          position: relative;
        }

        .avatar-circle.has-unread {
          background: #fff3cd;
          color: #856404;
          border: 2px solid #ff9800;
        }

        .cliente-info {
          flex: 1;
          min-width: 0;
        }

        .cliente-header {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          margin-bottom: 0.25rem;
        }

        .cliente-numero {
          font-weight: 500;
          color: #212529;
          font-size: 0.875rem;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
          max-width: 140px;
        }

        .cliente-time {
          font-size: 0.75rem;
          color: #6c757d;
          white-space: nowrap;
          margin-left: 0.5rem;
        }

        .cliente-mensajes-info {
          display: flex;
          justify-content: space-between;
          align-items: center;
          font-size: 0.75rem;
        }

        .mensaje-count {
          color: #6c757d;
        }

        .nuevo-mensaje-badge {
          font-size: 0.7rem;
          min-width: 20px;
          height: 20px;
          display: flex;
          align-items: center;
          justify-content: center;
          font-weight: 600;
        }

        /* Responsive */
        @media (max-width: 768px) {
          .cliente-item-content {
            padding: 1rem 0.75rem;
          }

          .cliente-numero {
            max-width: 120px;
          }

          .avatar-circle {
            width: 36px;
            height: 36px;
            font-size: 1.125rem;
          }
        }

        /* Animaciones suaves */
        .cliente-item {
          animation: fadeIn 0.2s ease-out;
        }

        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(5px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </div>
  );
};

export default React.memo(ClientesPanel);